/**
 * feat-270 AT-25 — the per-lesson chip in the 280px tree pane.
 *
 * `design/editor-integration.html` panel 2, `design/NOTES.md` §3a, and the
 * measurements in `design/tree-chip-measurements.json`.
 *
 * ⚠ **THE VISIBLE CONTENT OF THE NEVER-APPROVED CHIP IS THE GLYPH ALONE.** r1 of
 * the design brief specified `⚠ Not live` here and MEASURING KILLED IT: the full
 * label is 60.08px against a **157.09px** title budget — 42% of it — and it
 * truncated "Dubai Chocolate", a 15-character name that fitted before. At `⚠`
 * alone (22.00px) it fits again. The sentence moves to `title` **and**
 * `aria-label`, which is feat-256's own precedent in this exact row: *"the
 * `title` carries the precision `Later` omits, at zero pixels of row width, which
 * is the entire constraint."*
 *
 * A test asserting the visible TEXT will not see the sentence. Pin the
 * `aria-label`, and pin that the visible content is the glyph — otherwise a
 * well-meaning "let's spell it out" restores the 66px label and nothing fails.
 *
 * ⚠ **Do NOT add U+FE0F to the `⚠` and do not swap it for an emoji.** As written
 * it renders in TEXT presentation and therefore inherits `text-attention-on` like
 * any other glyph. The variation selector switches it to emoji presentation, at
 * which point the platform paints it its own red-orange and the token stops
 * governing the mark — a colour in the UI that no token can reach. It also
 * changes the width, so the 22.00px measurement stops holding.
 *
 * ⚠ **NO `min-w` HERE** (§10.2). feat-238's `min-w-[48px]` equal-width rule
 * governs the STATUS-PILL family, and this chip is not in it. A 48px floor buys
 * no alignment (nothing to its right is fixed) and costs the truncating title
 * 48px.
 *
 * The chip sits BEFORE the status pill, because approval is a SECOND AXIS
 * (§10.1): `active` and `available_at` read live and are never gated (spec D3),
 * so `On` + never-approved is a reachable and CORRECT combination — it is the
 * surprise this feature exists to surface. Never collapse the two into one pill.
 */

import React from 'react';
import { APPROVAL_EDITOR_COPY as E } from '../approvalCopy';
import type { LessonApprovalStatus } from '../../../../services/curriculumApprovalService';

export interface LessonApprovalChipProps {
  state: LessonApprovalStatus;
  pendingCount: number;
  lessonId: string;
}

export const LessonApprovalChip: React.FC<LessonApprovalChipProps> = ({
  state,
  pendingCount,
  lessonId,
}) => {
  // AT-25: zero pending renders NO badge. Approved is the state that draws
  // nothing at all — which is also the only way the 280px pane survives, since
  // the title is `flex-1 truncate` and everything added right of it comes off
  // the title.
  if (state === 'approved') return null;

  if (state === 'never_approved') {
    return (
      <span
        data-testid={`tree-row-approval-${lessonId}`}
        title={E.treeChipNeverApprovedLabel}
        aria-label={E.treeChipNeverApprovedLabel}
        className="bg-attention-surface text-attention-on text-[10px] font-bold rounded-full px-1.5 py-0.5 shrink-0"
      >
        {E.treeChipNeverApproved}
      </span>
    );
  }

  // Pending is NEUTRAL (NOTES §3): it is the frequent state, and colour spent on
  // what fires fifty times an hour leaves nothing for the one that doesn't.
  //
  // `● N` rather than a bare digit: the 9px saved is not worth the ambiguity next
  // to the drill row's own bare count chip, and the `●` is `ChangedDot`'s glyph,
  // which ties this chip to the diff vocabulary in the queue and History panel.
  // It is digit-driven and therefore ragged by construction — do NOT "fix" that
  // with a `min-w` (§10.2).
  if (pendingCount <= 0) return null;
  return (
    <span
      data-testid={`tree-row-approval-${lessonId}`}
      title={E.treeChipPendingLabel(pendingCount)}
      aria-label={E.treeChipPendingLabel(pendingCount)}
      className="bg-cream-dark text-inkMuted text-[10px] font-bold rounded-full px-1.5 py-0.5 shrink-0"
    >
      ● {pendingCount}
    </span>
  );
};
